import React, { useEffect, useState, useRef } from 'react';
import ReactQuill from 'react-quill-new';
import {
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Layout,
  message,
  Modal,
  Radio,
  Row,
  Select,
  Switch,
  Table,
  Typography,
  Upload,
  DatePicker,
} from 'antd';
import {
  DeleteOutlined,
  InboxOutlined,
  LoadingOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { get } from 'lodash';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import utc from 'dayjs/plugin/utc';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(utc);

import { PageSpinner } from 'components/page-spinner';
import PdButton from 'components/pd-button';
import ColorSection from 'components/productColor';
import SizeSection from 'components/productSize';
import SortableMedia from 'components/sortableProductMedia';

import ProductService from 'services/product';
import RentService from 'services/rent';
import StockService from 'services/stock';
import UploadService from 'services/upload';
import RiskManagementService from 'services/risk-management';
import WarehouseServices from 'services/warehouse';

import { errHandling } from 'utils';
import queryString from 'query-string';

import 'react-quill-new/dist/quill.snow.css';
import './style.scss';
import useDebounce from 'hooks/useDebounce';
import { useLocation, useNavigate } from 'react-router-dom';

const options = {
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  month: '2-digit',
  second: '2-digit',
  timeZone: 'Asia/Jakarta',
  timeZoneName: 'short',
  year: 'numeric',
};

const formatter = new Intl.DateTimeFormat('sv-SE', options);

const { Content } = Layout;
const { Option } = Select;
const { Title, Text } = Typography;

const FIELDS = {
  POS: 'pos_only',
  BRAND_ID: 'brand_id',
  DESCRIPTION: 'description',
  DISCOUNT_DETAIL: 'discount_detail',
  INSTALLABLE: 'installable',
  INSTALLATION_DEDUCTION_FIXED: 'installation_deduction_fixed',
  INSTALLATION_DEDUCTION_PERCENTAGE: 'installation_deduction_percentage',
  INSTALLATION_FEE: 'installation_fee',
  NON_STOCK_SKU: 'non_stock_sku',
  KEYWORDS: 'keyword',
  LIMIT: 'limit_per_order',
  POINT: 'point',
  PRICE: 'price',
  PRODUCT_CATEGORIES_ATTRIBUTES: 'product_categories_attributes',
  PRODUCT_COLORS_ATTRIBUTES: 'product_colors_attributes',
  PRODUCT_GALLERIES_ATTRIBUTES: 'product_galleries_attributes',
  QUANTITY: 'quantity',
  RENT_PRICINGS: 'rent_pricings',
  RISK_MANAGEMENT_ID: 'risk_management_id',
  SHORT_DESCRIPTION: 'short_description',
  SIZES: 'sizes',
  SKU: 'sku',
  STOCK: 'stock',
  TITLE: 'title',
  WEIGHT: 'weight',
};

const INSTALLATION_DISCOUNT_TYPE = {
  FIXED: 'installation_deduction_fixed',
  NONE: 'none',
  PERCENTAGE: 'installation_deduction_percentage',
};

const INSTALLABLE_OPTIONS = [
  { value: false, label: 'Non-Installable' },
  { value: true, label: 'Installable' },
];

const INSTALLABLE_DISCOUNT_TYPE_OPTIONS = [
  { value: INSTALLATION_DISCOUNT_TYPE.NONE, label: 'No Discount' },
  { value: INSTALLATION_DISCOUNT_TYPE.PERCENTAGE, label: 'Percentage' },
  { value: INSTALLATION_DISCOUNT_TYPE.FIXED, label: 'Fixed' },
];

const { RangePicker } = DatePicker;

export function ProductCreateEdit() {
  const [form] = Form.useForm();
  const [brandOption, setBrandOption] = useState([]);
  const [categoryOption, setCategoryOption] = useState([]);
  const [currentInstallationDiscountType, setCurrentInstallationDiscountType] =
    useState(INSTALLATION_DISCOUNT_TYPE.NONE);
  const [discountStatus, changeDiscountStatus] = useState('None');
  const [formLayout] = useState('vertical');
  const [isInstallable, setIsInstallable] = useState(false);
  const [latePenalty, setLatePenalty] = useState(0);
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingUpload, setLoadingUpload] = useState({});
  const [media, setMedia] = useState({});
  const [parentCategoryOption, setParentCategoryOption] = useState([]);
  const [parentId, setParentId] = useState({});
  const [rentDuration, setRentDuration] = useState([]);
  const [rentDurationPrices, setRentDurationPrices] = useState({});
  const [rentStatus, setRentStatus] = useState(false);
  const [riskDetail, setRiskDetail] = useState({});
  const [riskList, setRiskList] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedParent, setSelectedParent] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [showColorSection, setShowColorSection] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [sizeChanged, setSizeChanged] = useState(false);
  const [stockInfinity, setStockInfinity] = useState(false);
  const [subcategoryOption, setSubcategoryOption] = useState([]);
  const [value, setValue] = useState({});
  const [selectedDiscountArea, setSelectedDiscountArea] = useState([]);
  const [warehouseList, setWarehouseList] = useState([]);
  const [validateDiscountArea, setValidateDiscountArea] = useState([]);
  const isInitialLoad = useRef(true);
  const [searchDiscountArea, setSearchDiscountArea] = useState('');
  const debounceKeyword = useDebounce(searchDiscountArea, 200);
  const location = useLocation();
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(location.search);
  const id = urlParams.get('id');

  const formItemLayout =
    formLayout === 'horizontal'
      ? {
          labelCol: { span: 4 },
          wrapperCol: { span: 14 },
        }
      : null;

  const buttonItemLayout =
    formLayout === 'horizontal'
      ? {
          wrapperCol: { span: 14, offset: 4 },
        }
      : null;

  const modules = {
    toolbar: false,
  };

  const formats = [
    'blockquote',
    'bold',
    'bullet',
    'header',
    'image',
    'indent',
    'italic',
    'link',
    'list',
    'strike',
    'underline',
  ];

  const handlePercentageFormFormatter = (value) => {
    return `${value} %`;
  };

  const handlePercentageFormParser = (value) => {
    return value.replace(/[^0-9]\s?|(\.*)/g, '');
  };

  const handleInputNumberParser = (value) => {
    return value.replace(/[^0-9]\s?|(\.*)/g, '').replace(/^0+/, '');
  };

  const handleInputNumberFormatter = (value) => {
    return `IDR ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  async function getRentDurations() {
    try {
      const { data } = await RentService.getRentDurations();
      let allDuration = data.data;
      setRentDuration(
        allDuration.filter((duration) => duration.status !== 'inactive')
      );
    } catch (err) {
      errHandling(err);
    }
  }

  async function getBrands() {
    try {
      const { data } = await ProductService.getBrandList(parentId[0]);
      setBrandOption(data.data);
    } catch (err) {
      errHandling(err);
    }
  }

  async function getCategories() {
    try {
      const { data } = await ProductService.getCategoryList();
      setParentCategoryOption(data.data);
    } catch (err) {
      errHandling(err);
    }

    for (let [key, value] of Object.entries(parentId)) {
      try {
        const { data } = await ProductService.getCategoryList(value);
        if (key === '0') {
          setCategoryOption(data.data);
        } else if (key === '1') {
          setSubcategoryOption(data.data);
        }
      } catch (err) {
        errHandling(err);
      }
    }
  }

  async function beforeUpload(file, type, key, index, field) {
    try {
      const ext = file.type.split('/')[1];
      if (type === 'video') {
        if (ext !== 'mp4') {
          throw message.error('You can only upload MP4 file!');
        }
      }
      const { data } = await UploadService.getUploadURL(ext, 'product');
      const uploadURL = data.data;
      await awsUpload(file, key, index, field, type, uploadURL);
    } catch (err) {
      errHandling(err);
    }
  }

  async function awsUpload(file, key, index, field, type, uploadURL) {
    try {
      setLoadingUpload((prevState) => ({
        ...prevState,
        [key + field + index]: true,
      }));
      await UploadService.uploadImage(file, uploadURL.upload_url);
      onChangeModal(field, uploadURL.secure_url);
      if (type === 'image' && field === 'secure_url') {
        onChangeModal('thumbnail_image', uploadURL.secure_url);
      }
      message.success('Successfully uploaded');
    } catch (err) {
      errHandling(err);
    } finally {
      setLoadingUpload((prevState) => ({
        ...prevState,
        [key + field + index]: false,
      }));
    }
  }

  async function onSortEndMedia({ newIndex, oldIndex }) {
    let list = [...get(value, FIELDS.PRODUCT_GALLERIES_ATTRIBUTES, [])];
    let temp = list[oldIndex];
    list.splice(oldIndex, 1);
    list.splice(newIndex, 0, temp);
    list.forEach((item, index) => {
      list[index] = {
        ...item,
        order_level: index,
      };
    });
    setValue((prevState) => ({
      ...prevState,
      [FIELDS.PRODUCT_GALLERIES_ATTRIBUTES]: list,
    }));
  }

  async function getProduct() {
    try {
      if (id) {
        const { data } = await ProductService.getProductDetail(id);
        const temporaryDiscountDetail = JSON.parse(JSON.stringify(data.data));
        const productDetail = data.data;
        const size = productDetail[FIELDS.SIZES].map((size, i) => {
          size['order_level'] = i;
          size['_destroy'] = get(size, '_destroy', false);
          return size;
        });

        productDetail[FIELDS.SIZES] = size;
        productDetail['length'] = get(productDetail, 'dimensions.length', 0);
        productDetail['width'] = get(productDetail, 'dimensions.width', 0);
        productDetail['height'] = get(productDetail, 'dimensions.height', 0);
        productDetail['diameter'] = get(
          productDetail,
          'dimensions.diameter',
          0
        );

        if (
          productDetail[FIELDS.QUANTITY] === null ||
          productDetail[FIELDS.QUANTITY] < 0
        ) {
          setStockInfinity(true);
        }

        const {
          data: { data: { warehouse_stocks = [] } = {} },
        } = await StockService.getProductStock(id);
        productDetail[FIELDS.PRODUCT_COLORS_ATTRIBUTES] = productDetail[
          'color_variants'
        ].map((color, i) => {
          const { id: colorId } = color;
          const colorWarehouseStock = warehouse_stocks.map(
            ({ warehouse = {}, varians = [] }) => {
              let thereIsVariant = false;
              let quantityVariant = 0;
              const { quantity = null } =
                varians.find(
                  ({ color: { id: colorFindId = '' } = {} }) =>
                    colorId === colorFindId
                ) || {};
              if (quantity !== null) {
                thereIsVariant = true;
                quantityVariant = quantity;
              }
              return (
                thereIsVariant && {
                  warehouse,
                  quantity: quantityVariant,
                }
              );
            }
          );
          color['order_level'] = i;
          color['product_gallery_id'] = color['gallery_id'];
          color['_destroy'] = get(color, '_destroy', false);
          color['warehouses'] = colorWarehouseStock;
          return color;
        });
        productDetail[FIELDS.BRAND_ID] = productDetail['brand'].id;
        productDetail[FIELDS.PRODUCT_CATEGORIES_ATTRIBUTES] = productDetail[
          'categories'
        ].map((category) => {
          return { category_id: category.id };
        });
        productDetail[FIELDS.PRODUCT_GALLERIES_ATTRIBUTES] = productDetail[
          'media'
        ].map((media, index) => {
          return {
            id: media.id,
            title: media.title,
            secure_url: media.secure_url,
            url_type: media.url_type,
            thumbnail_image: media.thumbnail_image,
            order_level: index,
            _destroy: false,
          };
        });
        if (productDetail['brand']) {
          setSelectedBrand(productDetail['brand'].title);
        }
        if (productDetail['categories'][0]) {
          const optionParent = await ProductService.getCategoryList();
          setParentCategoryOption(optionParent.data.data);
          setSelectedParent(productDetail['categories'][0].title);
          setParentId((prevState) => ({
            ...prevState,
            0: productDetail['categories'][0].id,
          }));
        }
        if (productDetail['categories'][1]) {
          setSelectedCategory(productDetail['categories'][1].title);
          setParentId((prevState) => ({
            ...prevState,
            1: productDetail['categories'][1].id,
          }));
        }
        if (productDetail['categories'][2]) {
          setSelectedSubcategory(productDetail['categories'][2].title);
        }
        if (productDetail[FIELDS.DISCOUNT_DETAIL].length === 0) {
          changeDiscountStatus('None');
        } else {
          if (!productDetail[FIELDS.DISCOUNT_DETAIL][0].lower_price) {
            if (
              !productDetail[FIELDS.DISCOUNT_DETAIL][0].deduction_percentage
            ) {
              changeDiscountStatus('None');
            } else changeDiscountStatus('Percentage');
          } else changeDiscountStatus('Lower Price');
        }

        const discountArea = productDetail[FIELDS.DISCOUNT_DETAIL].map(
          (item, index) => ({
            acumatica_id: item.acumatica_id ? item.acumatica_id : '',
            deduction_percentage: item.deduction_percentage,
            lower_price: item.lower_price,
            id: item.warehouse_id === null ? 0 : item.warehouse_id,
            title: item.warehouse_title ? item.warehouse_title : '',
            quantity_limit: item.quantity_limit,
            name: item.title ? item.title : '',
            status: item.status ? item.status : '-',
            start_date: dayjs(item.start_date).utc(),
            end_date: dayjs(item.end_date).utc(),
            whole_year:
              new Date(item.start_date).getTime() ===
                new Date(
                  `${new Date().getFullYear()}-01-01T00:00:00Z`
                ).getTime() &&
              new Date(item.end_date).getTime() ===
                new Date(
                  `${new Date().getFullYear()}-12-31T23:59:59Z`
                ).getTime(),
            no: index + 1,
          })
        );

        setSelectedDiscountArea(discountArea);

        if (productDetail[FIELDS.DISCOUNT_DETAIL].length > 0) {
          delete productDetail[FIELDS.DISCOUNT_DETAIL];
          if (temporaryDiscountDetail[FIELDS.DISCOUNT_DETAIL][0].start_date) {
            let newStartDate = formatter.format(
              new Date(
                temporaryDiscountDetail[FIELDS.DISCOUNT_DETAIL][0].start_date
              )
            );
            productDetail[FIELDS.DISCOUNT_DETAIL] = {
              ...productDetail[FIELDS.DISCOUNT_DETAIL],
              start_date: newStartDate,
            };
          }

          if (temporaryDiscountDetail[FIELDS.DISCOUNT_DETAIL][0].end_date) {
            let newEndDate = formatter.format(
              new Date(
                temporaryDiscountDetail[FIELDS.DISCOUNT_DETAIL][0].end_date
              )
            );
            productDetail[FIELDS.DISCOUNT_DETAIL] = {
              ...productDetail[FIELDS.DISCOUNT_DETAIL],
              end_date: newEndDate,
            };
          }
        }

        isInitialLoad.current = true;
        setValue(productDetail);
        setRentStatus(productDetail.rented);
        setIsInstallable(productDetail.installable);
        if (productDetail.installable) {
          const {
            installation_deduction_percentage,
            installation_deduction_fixed,
          } = productDetail;

          let installationDiscountType = INSTALLATION_DISCOUNT_TYPE.NONE;

          if (installation_deduction_percentage !== null) {
            installationDiscountType = INSTALLATION_DISCOUNT_TYPE.PERCENTAGE;
          } else if (installation_deduction_fixed !== null) {
            installationDiscountType = INSTALLATION_DISCOUNT_TYPE.FIXED;
          }
          setCurrentInstallationDiscountType(installationDiscountType);
        }
        if (productDetail.rented) {
          let rentPrice = {};
          get(productDetail, 'rent_pricings', []).forEach((durationPrice) => {
            let durationId = get(durationPrice, 'rent_duration.id', 0);
            rentPrice[durationId] = {
              price: Number(get(durationPrice, 'price', 0)),
              id: get(durationPrice, 'id', 0),
            };
          });
          setRentDurationPrices(rentPrice);
        }
        if (productDetail.risk.id) {
          getRiskDetail(productDetail.risk.id);
        }
      }
    } finally {
      setLoadingPage(false);
    }
  }

  function handleDurationPrice(id, price) {
    let tempDurationPrice = rentDurationPrices;

    // if price exist then update else add new renting price
    if (rentDurationPrices[id]) {
      tempDurationPrice[id].price = price;
    } else {
      tempDurationPrice[id] = {
        price: price,
      };
    }
    setRentDurationPrices(tempDurationPrice);
  }

  function addMedia() {
    setMedia({
      title: '',
      secure_url: '',
      url_type: 'image',
      thumbnail_image: '',
      order_level: get(value, FIELDS.PRODUCT_GALLERIES_ATTRIBUTES, []).length,
      index: get(value, FIELDS.PRODUCT_GALLERIES_ATTRIBUTES, []).length,
      modal_type: 'New Media',
    });
    setShowModal(true);
  }

  function removeMedia(index) {
    Modal.confirm({
      title: 'Are you sure you want to delete this media?',
      onOk() {
        let newMedia = [...value[FIELDS.PRODUCT_GALLERIES_ATTRIBUTES]];
        newMedia[index]['_destroy'] = true;
        setValue((prevState) => ({
          ...prevState,
          [FIELDS.PRODUCT_GALLERIES_ATTRIBUTES]: newMedia,
        }));
      },
    });
  }

  function editMedia(index) {
    let data = get(value, FIELDS.PRODUCT_GALLERIES_ATTRIBUTES, [])[index];
    setMedia({
      ...data,
      index,
      modal_type: 'Edit Media',
    });
    setShowModal(true);
  }

  function hasErrors() {
    const errors = form.getFieldsError();
    return errors.some(({ errors }) => errors.length > 0);
  }

  async function handleSubmit(values) {
    try {
      // values are passed directly by onFinish in antd v5
      const error = {};

      if (discountStatus !== 'None') {
        selectedDiscountArea.forEach((area) => {
          if (dayjs(area.end_date).isBefore(dayjs())) return;
          if (
            !area.quantity_limit ||
            !area.lower_price ||
            !area.deduction_percentage
          ) {
            error[area.title] = {};
            if (!area.quantity_limit) {
              error[area.title].quantity_limit = 'Please input quantity!';
            }

            if (discountStatus === 'Percentage' && !area.deduction_percentage) {
              error[area.title].deduction_percentage =
                'Please input deduction percentage!';
            }
            if (discountStatus === 'Lower Price' && !area.lower_price) {
              error[area.title].lower_price = 'Please input lower price!';
            }
          }
        });

        setValidateDiscountArea(error);
      }

      const allPropertiesAreEmpty = Object.keys(error).every((city) => {
        const properties = Object.keys(error[city]);
        return properties.length === 0;
      });

      if (!allPropertiesAreEmpty) {
        return;
      }

      try {
        const payloadDiscount = {
          discount_areas_attributes: [
            {
              discount_warehouses_attributes: selectedDiscountArea.map(
                (area) => ({
                  warehouse_id: area.id === 0 ? null : area.id,
                  deduction_percentage: area.deduction_percentage,
                  lower_price: area.lower_price,
                  quantity_limit: area.quantity_limit,
                  title: area.name,
                  start_date: area.whole_year
                    ? dayjs(`${dayjs().year()}-01-01`).utc().toISOString()
                    : area.start_date,
                  end_date: area.whole_year
                    ? dayjs(`${dayjs().year()}-12-31`).utc().toISOString()
                    : area.end_date,
                })
              ),
            },
          ],
        };

        if (values[FIELDS.QUANTITY]) {
          value[FIELDS.QUANTITY] = null;
        }
        if (stockInfinity) {
          values[FIELDS.QUANTITY] = -1;
        }
        const deletedImage = [];
        get(value, FIELDS.PRODUCT_GALLERIES_ATTRIBUTES, []).forEach((x) => {
          if (x._destroy) {
            deletedImage.push(x.id);
          }
        });
        const filteredColor = get(
          value,
          FIELDS.PRODUCT_COLORS_ATTRIBUTES,
          []
        ).map((x) => {
          if (deletedImage.includes(x.product_gallery_id)) {
            x.product_gallery_id = null;
          }
          return x;
        });
        value[FIELDS.PRODUCT_COLORS_ATTRIBUTES] = filteredColor;
        if (values['lower_price'] === null) {
          values['lower_price'] = 0;
        }
        let sizeRelation = {};
        let relations = [];
        get(value, FIELDS.SIZES, []).forEach((x, i) => {
          if (i === 0) {
            sizeRelation['size'] = x.size;
          }
          if (x.product_id !== null && x.product_id !== undefined) {
            relations.push({
              _destroy: x._destroy,
              relation_product_id: x.product_id,
              relation_size: x.size,
              order_level: x.order_level,
            });
          }
        });
        sizeRelation['relations'] = relations;
        setLoadingButton(true);
        if (!get(value, FIELDS.PRODUCT_CATEGORIES_ATTRIBUTES, [])[0]) {
          const obj = {
            response: {
              data: {
                message: 'Please select Parent Category',
              },
            },
          };
          throw obj;
        }
        if (!get(value, FIELDS.BRAND_ID, '')) {
          const obj = {
            response: {
              data: {
                message: 'Please select Brand',
              },
            },
          };
          throw obj;
        }
        let product_rent_pricings_attributes = [];
        const stringified = queryString.stringify(
          get(location, 'state.parsed', {})
        );
        const prevPath = rentStatus ? '/rent_products' : '/products';
        if (rentStatus) {
          for (const durationId in rentDurationPrices) {
            // check for price is update or add new
            let rentPricing = {};
            if (rentDurationPrices[durationId].id) {
              rentPricing = {
                id: get(rentDurationPrices[durationId], 'id', ''),
                price: get(rentDurationPrices[durationId], 'price', 0),
              };
            } else {
              rentPricing = {
                rent_duration_id: durationId,
                price: get(rentDurationPrices[durationId], 'price', 0),
              };
            }
            product_rent_pricings_attributes.push(rentPricing);
          }
        }
        if (
          rentStatus &&
          product_rent_pricings_attributes.length < rentDuration.length
        ) {
          const obj = {
            response: {
              data: {
                message: `Please input the price for rent product or change the product to 'Non-rentable'`,
              },
            },
          };
          throw obj;
        }
        const installationAttr = {
          installable: isInstallable,
          installation_deduction_fixed:
            currentInstallationDiscountType === INSTALLATION_DISCOUNT_TYPE.FIXED
              ? value[INSTALLATION_DISCOUNT_TYPE.FIXED]
              : null,
          installation_deduction_percentage:
            currentInstallationDiscountType ===
            INSTALLATION_DISCOUNT_TYPE.PERCENTAGE
              ? value[INSTALLATION_DISCOUNT_TYPE.PERCENTAGE]
              : null,
        };

        const dimensions = {
          height: get(values, 'height', 0),
          length: get(values, 'length', 0),
          width: get(values, 'width', 0),
          diameter: get(values, 'diameter', 0),
        };

        if (id) {
          try {
            if (sizeChanged) {
              await ProductService.addRelations(id, sizeRelation);
            }
            delete value[FIELDS.SIZES];
            delete value[FIELDS.DISCOUNT_DETAIL];
            values.weight = Number(values.weight) * 1000;
            await ProductService.updateProduct(id, {
              ...value,
              ...values,
              daily_penalty: latePenalty,
              keywords: keywordHandler(values[FIELDS.KEYWORDS]),
              point: 1,
              product_rent_pricings_attributes,
              rented: rentStatus,
              ...installationAttr,
              ...(discountStatus === 'None'
                ? { discount_areas_attributes: [] }
                : { ...payloadDiscount }),
              dimensions,
            });
            message.success('Successfully updated product');
            navigate(prevPath + '?' + stringified);
          } catch (error) {
            errHandling(error);
          }
        } else {
          try {
            delete value[FIELDS.DISCOUNT_DETAIL];
            values.weight = Number(values.weight) * 1000;
            await ProductService.createProduct({
              ...value,
              ...values,
              daily_penalty: latePenalty,
              keywords: keywordHandler(values[FIELDS.KEYWORDS]),
              point: 1,
              product_rent_pricings_attributes,
              rented: rentStatus,
              ...installationAttr,
              ...(discountStatus === 'None'
                ? { discount_areas_attributes: [] }
                : { ...payloadDiscount }),
              dimensions,
            });
            message.success('Successfully created product');
            navigate(prevPath + '?' + stringified);
          } catch (error) {
            errHandling(error);
          }
        }
      } catch (err) {
        errHandling(err);
      } finally {
        setLoadingButton(false);
      }
    } catch {
      // Form validation failed - will be handled by antd form validation
    }
  }

  function changeValue(key, e) {
    if (e !== undefined && e !== null) {
      setValue((prevState) => ({
        ...prevState,
        [key]: typeof e === 'number' ? parseFloat(e.toFixed(4)) : e,
      }));
    }
  }

  function handleOnChange(key, val, index, field) {
    if (val && key !== FIELDS.PRODUCT_CATEGORIES_ATTRIBUTES) {
      if (key === FIELDS.BRAND_ID) {
        const brand = brandOption.find((x) => x.title === val);
        setValue((prevState) => ({
          ...prevState,
          [key]: brand.id,
        }));
      } else if (key === FIELDS.PRODUCT_GALLERIES_ATTRIBUTES) {
        let newValue = [...value[key]];
        if (field === 'title' || field === 'url_type') {
          newValue[index][field] = val.target.value;
        } else {
          if (newValue[index]['url_type'] === 'link') {
            newValue[index][field] = val.target.value;
          } else {
            newValue[index][field] = val;
          }
        }
        setValue((prevState) => ({
          ...prevState,
          [key]: newValue,
        }));
      } else {
        setValue((prevState) => ({
          ...prevState,
          [key]: val,
        }));
      }
    } else if (key === FIELDS.PRODUCT_CATEGORIES_ATTRIBUTES) {
      setSelectedBrand('');

      let newValue = [];
      if (value[key]) {
        newValue = value[key];
      }
      let collection = [];
      if (index === 0) {
        collection = parentCategoryOption;
        form.setFieldsValue({ brand: '' });
        setSelectedParent(val);
        setSelectedCategory('');
        setSelectedSubcategory('');
      } else if (index === 1) {
        collection = categoryOption;
        setSelectedCategory(val);
        setSelectedSubcategory('');
      } else {
        collection = subcategoryOption;
        setSelectedSubcategory(val);
      }
      if (val) {
        const category = collection.find((x) => x.title === val);
        newValue[index] = { category_id: category.id };
        setValue((prevState) => ({
          ...prevState,
          [key]: newValue,
        }));
        setParentId((prevState) => ({
          ...prevState,
          [index]: category.id,
        }));
      } else {
        setValue((prevState) => ({
          ...prevState,
          [key]: newValue.slice(0, index),
        }));
      }
    }
  }

  function onChangeModal(field, val) {
    if (val) {
      let value;
      if (field === 'title' || field === 'url_type') {
        value = val.target.value;
      } else {
        if (media['url_type'] === 'link') {
          value = val.target.value;
          let thumbnail_image = value;
          // generate youtube thumbnail
          if (
            thumbnail_image.includes('youtube') ||
            thumbnail_image.includes('youtu.be')
          ) {
            let youtubeId = thumbnail_image.split('/').pop();
            // if id wrapped by query params w=id
            if (youtubeId.includes('=')) {
              youtubeId = youtubeId.split('=').pop();
            }
            thumbnail_image = `http://img.youtube.com/vi/${youtubeId}/0.jpg`;
          }
          setMedia((prevState) => ({
            ...prevState,
            thumbnail_image,
          }));
        } else {
          value = val;
        }
      }
      setMedia((prevState) => ({
        ...prevState,
        [field]: value,
      }));
    }
  }

  function handleCancel() {
    setMedia({});
    setShowModal(false);
  }

  function handleOk() {
    let error = false;
    let msg;
    Object.keys(media)
      .reverse()
      .forEach((key) => {
        if (media[key] === '') {
          switch (key) {
            case 'title':
              error = true;
              msg = "Title can't be empty";
              break;
            case 'secure_url':
              error = true;
              msg = "Media can't be empty";
              break;
            case 'thumbnail_image':
              error = true;
              msg = "Media can't be empty";
              break;
            case 'url_type':
              error = true;
              msg = "Media type can't be empty";
              break;
            default:
              msg = '';
          }
        }
      });
    if (showModal && !error) {
      let list = [...get(value, FIELDS.PRODUCT_GALLERIES_ATTRIBUTES, [])];
      list[media.index] = media;
      setValue((prevState) => ({
        ...prevState,
        [FIELDS.PRODUCT_GALLERIES_ATTRIBUTES]: list,
      }));
      setMedia({});
      setShowModal(false);
    } else {
      message.error(msg);
    }
  }

  function onCheckStock(e) {
    setShowColorSection(e.target.checked);
  }

  function changeRentStatus(e) {
    setRentStatus(e.target.value);
  }

  function handleOnChangeWithThreeParams(key, index, val) {
    return handleOnChange(key, val, index);
  }

  function handleOnChangeDiscountForm(e) {
    changeDiscountStatus(e.target.value);
  }

  async function fetchRiskManagementList() {
    try {
      const {
        data: { data },
      } = await RiskManagementService.getRiskList({ status: 'active' });
      setRiskList(data);
    } catch (error) {
      errHandling(error);
    }
  }

  async function getRiskDetail(riskId) {
    try {
      const {
        data: { data },
      } = await RiskManagementService.getRiskDetail(riskId);
      setRiskDetail(data);
      setValue((prevState) => ({
        ...prevState,
        [FIELDS.RISK_MANAGEMENT_ID]: data.id,
      }));
    } catch (error) {
      errHandling(error);
    }
  }

  async function fetchWarehouse() {
    try {
      const {
        data: { data = [] },
      } = await WarehouseServices.getWarehouseList({ status: 'active' });

      const newData = [
        {
          id: 0,
          title: 'All Warehouse',
        },
        ...data,
      ];
      setWarehouseList(newData);
    } catch (error) {
      errHandling(error);
    }
  }

  function populateDepositRisk(durationId) {
    if (!value.price) {
      return '';
    }
    let depositType = riskDetail.risk_type;
    const riskDurationList = get(riskDetail, 'rent_duration_risks', []);
    const matchedDuration = riskDurationList.find(
      (_durationRisk) => _durationRisk.rent_duration.id === durationId
    );
    if (matchedDuration) {
      return depositType === 'fixed'
        ? matchedDuration.deposit_fixed
        : (matchedDuration.deposit_percentage * value.price) / 100;
    } else {
      return '';
    }
  }

  function keywordHandler(str = '') {
    return str
      .split(',')
      .map((str) => str.trim())
      .filter((str) => str !== '')
      .join(', ');
  }

  const keywordsLabel = (
    <span className="keywords-note">
      Separate each keyword by comma. Example: kirin, cosmetic
    </span>
  );

  const columns = [
    {
      title: <PlusOutlined style={{ cursor: 'pointer' }} />,
      key: Math.random.toString(),
      onHeaderCell: () => ({
        onClick: () => {
          const newData = [...selectedDiscountArea];
          newData.push({
            whole_year: false,
            name: '',
            title: '',
            no: newData.length + 1,
          });
          setSelectedDiscountArea(newData);
        },
      }),
      render: (_data, _record, index) => index + 1,
      width: 50,
    },
    {
      title: 'Discount Area',
      dataIndex: 'title',
      key: Math.random.toString(),
      /* eslint-disable react/display-name */
      render: (_data, record, index) => {
        return (
          <Select
            placeholder="Select Warehouse"
            value={record.id}
            onChange={(value) => {
              const newData = [...selectedDiscountArea];
              const selectedWarehouse = warehouseList.find(
                (warehouse) => warehouse.id === value
              );
              newData[index] = { ...newData[index], ...selectedWarehouse };
              setSelectedDiscountArea(newData);
            }}>
            {warehouseList.map((warehouse) => (
              <Option
                key={warehouse.id}
                value={warehouse.id}
                label={warehouse.title}>
                {warehouse.title}
              </Option>
            ))}
          </Select>
        );
      },
      width: 200,
    },
    {
      title: 'Promo Name',
      key: Math.random.toString(),
      /* eslint-disable react/display-name */
      render: (data) => {
        const newData = [...selectedDiscountArea];
        const index = newData.findIndex((item) => item.no === data.no);
        return (
          <Input
            placeholder="Promo name"
            onChange={(e) => {
              newData[index].name = e.target.value;
              setSelectedDiscountArea(newData);
            }}
            value={selectedDiscountArea[index].name}
          />
        );
      },
      width: 250,
    },
    {
      title: 'Status',
      key: Math.random.toString(),
      render: (_data, record) => {
        const statusColor = record.status === 'active' ? '#52c41a' : '#ff4d4f';
        return (
          <span
            style={{
              color: statusColor,
              fontWeight: 500,
              textTransform: 'capitalize',
            }}>
            {record.status || 'active'}
          </span>
        );
      },
      width: 120,
    },
    {
      title: discountStatus,
      key: Math.random.toString(),
      /* eslint-disable react/display-name */
      render: (data) => {
        const newSelectedDiscountArea = [...selectedDiscountArea];
        const index = newSelectedDiscountArea.findIndex(
          (item) => item.no === data.no
        );

        return (
          <div className="rule-form">
            {discountStatus === 'Percentage' ? (
              <InputNumber
                className="inputForm input"
                value={selectedDiscountArea[index].deduction_percentage}
                formatter={handlePercentageFormFormatter}
                max={100}
                min={0}
                onChange={(value) => {
                  newSelectedDiscountArea[index].deduction_percentage = value;
                  newSelectedDiscountArea[index].lower_price = null;
                  setSelectedDiscountArea(newSelectedDiscountArea);
                }}
                parser={handlePercentageFormParser}
                placeholder="Write percentage deduction here"
                step={1}
              />
            ) : (
              <InputNumber
                className="inputForm input"
                value={selectedDiscountArea[index].lower_price}
                formatter={handleInputNumberFormatter}
                min={0}
                onChange={(value) => {
                  newSelectedDiscountArea[index].lower_price = value;
                  newSelectedDiscountArea[index].deduction_percentage = null;
                  setSelectedDiscountArea(newSelectedDiscountArea);
                }}
                parser={handleInputNumberParser}
                placeholder="Write lower price here"
                step={1}
              />
            )}
            <div
              style={{
                color: 'red',
              }}>
              {validateDiscountArea[data.title] &&
              validateDiscountArea[data.title][
                discountStatus === 'Percentage'
                  ? 'deduction_percentage'
                  : 'lower_price'
              ]
                ? validateDiscountArea[data.title][
                    discountStatus === 'Percentage'
                      ? 'deduction_percentage'
                      : 'lower_price'
                  ]
                : ''}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Limit',
      key: Math.random.toString(),
      render: (data) => {
        const newSelectedDiscountArea = [...selectedDiscountArea];

        const index = newSelectedDiscountArea.findIndex(
          (item) => item.no === data.no
        );
        return (
          <div className="rule-form">
            <InputNumber
              className="inputForm input"
              value={selectedDiscountArea[index].quantity_limit}
              min={0}
              onChange={(value) => {
                newSelectedDiscountArea[index].quantity_limit = value;
                setSelectedDiscountArea(newSelectedDiscountArea);
              }}
              parser={handleInputNumberParser}
              placeholder="Input your limit"
              step={1}
            />
            <div
              style={{
                color: 'red',
              }}>
              {typeof validateDiscountArea[data.title] === 'object' &&
              'quantity_limit' in validateDiscountArea[data.title]
                ? validateDiscountArea[data.title].quantity_limit
                : ''}
            </div>
          </div>
        );
      },
      width: 200,
    },
    {
      title: 'Duration (in WIB)',
      key: Math.random.toString(),
      render: (data) => {
        const newSelectedDiscountArea = [...selectedDiscountArea];
        const index = newSelectedDiscountArea.findIndex(
          (item) => item.no === data.no
        );
        return (
          <RangePicker
            disabled={selectedDiscountArea[index].whole_year}
            showTime={{ format: 'HH:mm' }}
            format="YYYY-MM-DD HH:mm"
            onChange={(dates) => {
              newSelectedDiscountArea[index].start_date = dayjs(
                dates[0]
              ).format('YYYY-MM-DD HH:mm');
              newSelectedDiscountArea[index].end_date = dayjs(dates[1]).format(
                'YYYY-MM-DD HH:mm'
              );
              setSelectedDiscountArea(newSelectedDiscountArea);
            }}
            value={
              selectedDiscountArea[index].start_date
                ? [
                    dayjs(selectedDiscountArea[index].start_date),
                    dayjs(selectedDiscountArea[index].end_date),
                  ]
                : [null, null]
            }
          />
        );
      },
      width: 400,
    },
    {
      title: '',
      key: Math.random.toString(),
      render: (data) => {
        const newData = [...selectedDiscountArea];
        const index = newData.findIndex((item) => item.no === data.no);
        return (
          <div
            style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
            }}>
            <Checkbox
              onChange={(e) => {
                newData[index].whole_year = e.target.checked;
                setSelectedDiscountArea(newData);
              }}
              checked={selectedDiscountArea[index].whole_year}
            />
            <div
              style={{
                fontSize: '14px',
                color: '#232F40',
              }}>
              Whole Year
            </div>
          </div>
        );
      },
      width: 150,
    },
    {
      title: '',
      key: Math.random.toString(),
      render: (_data, _record, index) => {
        return (
          <DeleteOutlined
            type="delete"
            style={{ cursor: 'pointer' }}
            onClick={() =>
              setSelectedDiscountArea((prev) =>
                prev.filter((_d, i) => i !== index)
              )
            }
          />
        );
      },
      width: 50,
    },
  ];

  useEffect(() => {
    getProduct();
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    fetchRiskManagementList();
    fetchWarehouse();
  }, []);

  useEffect(() => {
    getBrands();
    getCategories();
    getRentDurations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentId]);

  useEffect(() => {
    const productColorAttr = value[FIELDS.PRODUCT_COLORS_ATTRIBUTES] || [];
    setShowColorSection(!!productColorAttr.length);
    // eslint-disable-next-line
  }, [value[FIELDS.PRODUCT_COLORS_ATTRIBUTES]]);

  // Update form fields when value changes (only on initial load)
  useEffect(() => {
    // Only update form on initial load from API, not from user input
    if (!isInitialLoad.current) return;

    const weightValue = Number(get(value, FIELDS.WEIGHT, 0));
    const lengthValue = Number(get(value, 'length', 0));
    const widthValue = Number(get(value, 'width', 0));
    const heightValue = Number(get(value, 'height', 0));

    form.setFieldsValue({
      [FIELDS.TITLE]: value[FIELDS.TITLE],
      [FIELDS.POS]: value[FIELDS.POS],
      [FIELDS.WEIGHT]: weightValue > 0 ? Math.round((weightValue / 1000) * 10000) / 10000 : null,
      length: lengthValue > 0 ? Math.round(lengthValue * 10000) / 10000 : null,
      width: widthValue > 0 ? Math.round(widthValue * 10000) / 10000 : null,
      height: heightValue > 0 ? Math.round(heightValue * 10000) / 10000 : null,
      diameter:
        get(value, 'diameter', 0) > 0 ? get(value, 'diameter', 0) : null,
      [FIELDS.SKU]: value[FIELDS.SKU],
      [FIELDS.QUANTITY]:
        get(value, FIELDS.QUANTITY, null) !== null &&
        Number(get(value, FIELDS.QUANTITY, null)) >= 0
          ? Number(get(value, FIELDS.QUANTITY, 0))
          : '',
      [FIELDS.LIMIT]:
        get(value, FIELDS.LIMIT, null) !== null &&
        Number(get(value, FIELDS.LIMIT, null)) >= 0
          ? Number(get(value, FIELDS.LIMIT, 0))
          : '',
      brand: selectedBrand,
      [FIELDS.KEYWORDS]: get(value, FIELDS.KEYWORDS, ''),
      [FIELDS.PRICE]: get(value, FIELDS.PRICE, 0),
      [FIELDS.INSTALLATION_FEE]: get(value, FIELDS.INSTALLATION_FEE, 0),
      [FIELDS.NON_STOCK_SKU]: get(value, FIELDS.NON_STOCK_SKU, null),
    });

    // Mark initial load as complete
    isInitialLoad.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Update brand field separately when selectedBrand changes
  useEffect(() => {
    if (selectedBrand !== undefined) {
      form.setFieldsValue({
        brand: selectedBrand,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand]);

  if (loadingPage) {
    return <PageSpinner />;
  }

  return (
    <Layout className="pd-cms-products main-container">
      <div className="container">
        <div className="content">
          <div className="pageTitle">{id ? 'Edit' : 'Create New'} Product</div>
          <Content>
            <Modal
              onCancel={handleCancel}
              onOk={handleOk}
              title={media.modal_type}
              open={showModal}>
              <Row gutter={[24, 24]}>
                <Col span={24}>
                  <div className="ant-form-item gap">
                    <label>{'Type'}</label>
                    <Radio.Group
                    vertical
                    options={[{value :'image', label: "Image"}, {value : 'video', label: "Video"}, {value : 'link', label: "Link"}]}
                      onChange={onChangeModal.bind(this, 'url_type')}
                      value={media.url_type}>
                    </Radio.Group>
                  </div>
                </Col>
                <Col span={24}>
                {media.url_type === 'image' && (
                  <div className="ant-form-item gap">
                    <label>{'Media Image'}</label>
                    <Upload.Dragger
                      name="Media Image"
                      showUploadList={false}
                      beforeUpload={(file) =>
                        beforeUpload(
                          file,
                          'image',
                          FIELDS.PRODUCT_GALLERIES_ATTRIBUTES,
                          media.index,
                          'secure_url'
                        )
                      }>
                      {media.secure_url ? (
                        loadingUpload[
                          FIELDS.PRODUCT_GALLERIES_ATTRIBUTES +
                            'secure_url' +
                            media.index
                        ] ? (
                          loadingUpload[
                            FIELDS.PRODUCT_GALLERIES_ATTRIBUTES +
                              'secure_url' +
                              media.index
                          ] ? (
                            <LoadingOutlined />
                          ) : (
                            <PlusOutlined />
                          )
                        ) : (
                          <img
                            alt={media.title + ' image'}
                            className="w-80"
                            src={media.secure_url}
                          />
                        )
                      ) : (
                        <>
                          <p className="ant-upload-drag-icon">
                            {loadingUpload[
                              FIELDS.PRODUCT_GALLERIES_ATTRIBUTES +
                                'secure_url' +
                                media.index
                            ] ? (
                              <LoadingOutlined />
                            ) : (
                              <InboxOutlined />
                            )}
                          </p>
                          <p className="ant-upload-text">
                            Select or drag a picture here to upload
                          </p>
                        </>
                      )}
                    </Upload.Dragger>
                  </div>
                )}
                </Col>

                {media.url_type === 'video' && (
                  <>
                    <Col span={12}>
                      <div className="ant-form-item gap">
                        <label>{'Media Video'}</label>
                        <Upload.Dragger
                        style={{height : "100%"}}
                          name="Media Video"
                          beforeUpload={(file) =>
                            beforeUpload(
                              file,
                              'video',
                              FIELDS.PRODUCT_GALLERIES_ATTRIBUTES,
                              media.index,
                              'secure_url'
                            )
                          }
                          showUploadList={false}>
                          {media.secure_url ? (
                            loadingUpload[
                              FIELDS.PRODUCT_GALLERIES_ATTRIBUTES +
                                'secure_url' +
                                media.index
                            ] ? (
                              loadingUpload[
                                FIELDS.PRODUCT_GALLERIES_ATTRIBUTES +
                                  'secure_url' +
                                  media.index
                              ] ? (
                                <LoadingOutlined />
                              ) : (
                                <PlusOutlined />
                              )
                            ) : (
                              <img
                                alt={media.title + ' image'}
                                className="w-80"
                                src={media.secure_url}
                              />
                            )
                          ) : (
                            <>
                              <p className="ant-upload-drag-icon">
                                {loadingUpload[
                                  FIELDS.PRODUCT_GALLERIES_ATTRIBUTES +
                                    'secure_url' +
                                    media.index
                                ] ? (
                                  <LoadingOutlined />
                                ) : (
                                  <InboxOutlined />
                                )}
                              </p>
                              <p className="ant-upload-text">
                                Pilih atau Seret kesini untuk upload
                                Video
                              </p>
                            </>
                          )}
                        </Upload.Dragger>
                      </div>
                    </Col>

                    <Col span={12}>
                      <div className="ant-form-item gap">
                        <label>{'Media Thumbnail'}</label>
                        <Upload.Dragger
                          name="Media Thumbnail"
                          beforeUpload={(file) =>
                            beforeUpload(
                              file,
                              'image',
                              FIELDS.PRODUCT_GALLERIES_ATTRIBUTES,
                              media.index,
                              'thumbnail_image'
                            )
                          }
                          showUploadList={false}>
                          {media.thumbnail_image ? (
                            loadingUpload[
                              FIELDS.PRODUCT_GALLERIES_ATTRIBUTES +
                                'thumbnail_image' +
                                media.index
                            ] ? (
                              loadingUpload[
                                FIELDS.PRODUCT_GALLERIES_ATTRIBUTES +
                                  'thumbnail_image' +
                                  media.index
                              ] ? (
                                <LoadingOutlined />
                              ) : (
                                <PlusOutlined />
                              )
                            ) : (
                              <img
                                alt={media.title + ' image'}
                                className="w-80"
                                src={media.thumbnail_image}
                              />
                            )
                          ) : (
                            <>
                              <p className="ant-upload-drag-icon">
                                {loadingUpload[
                                  FIELDS.PRODUCT_GALLERIES_ATTRIBUTES +
                                    'thumbnail_image' +
                                    media.index
                                ] ? (
                                  <LoadingOutlined />
                                ) : (
                                  <InboxOutlined />
                                )}
                              </p>
                              <p className="ant-upload-text">
                                Pilih atau Seret kesini untuk upload
                                Picture
                              </p>
                            </>
                          )}
                        </Upload.Dragger>
                      </div>
                    </Col>
                  </>
                )}

                {media.url_type === 'link' && (
                   <Col span={24}>

                  <div className="ant-form-item gap">
                    <label>{'Media Link'}</label>
                    <Input
                      placeholder="Media Link"
                      onChange={onChangeModal.bind(this, 'secure_url')}
                      defaultValue={media.secure_url}
                      />
                  </div>
                      </Col>
                )}

                <Col span={24}>
                  <div className="ant-form-item gap">
                    <label>{'Title'}</label>
                    <Input
                      placeholder="Media Title"
                      onChange={onChangeModal.bind(this, 'title')}
                      value={media.title}
                    />
                  </div>
                </Col>
              </Row>
            </Modal>

            <Form form={form} layout={formLayout} onFinish={handleSubmit}>
              <div className="section-container">
                <div className="inner-section">
                  <Title className="pageSubtitle" level={3}>
                    Product Information
                  </Title>
                  <Form.Item
                    label="Title"
                    {...formItemLayout}
                    name={FIELDS.TITLE}
                    rules={[
                      { message: 'Please input Title!', required: true },
                    ]}>
                    <Input
                      placeholder="Write product name here"
                      onChange={(e) =>
                        changeValue(FIELDS.TITLE, e.target.value)
                      }
                    />
                  </Form.Item>
                  <Form.Item
                    label="Product POS"
                    {...formItemLayout}
                    name={FIELDS.POS}>
                    <Switch
                      onChange={(e) =>
                        setValue((prevState) => ({
                          ...prevState,
                          [FIELDS.POS]: e,
                        }))
                      }
                      checked={value[FIELDS.POS]}
                    />
                    <Text strong style={{ marginLeft: 8 }}>
                      Is {value[FIELDS.POS] ? 'On' : 'Off'}
                    </Text>
                  </Form.Item>
                  <Row gutter={20}>
                    <Col span={12}>
                      <Form.Item
                        label="Weight (in kg)"
                        {...formItemLayout}
                        name={FIELDS.WEIGHT}
                        rules={[
                          { required: true, message: 'Please input Weight!' },
                        ]}>
                        <InputNumber
                          className="inputForm"
                          min={0}
                          placeholder="0"
                          step={0.001}
                          controls={true}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Length (in cm)"
                        {...formItemLayout}
                        name="length">
                        <InputNumber
                          className="inputForm"
                          min={0}
                          placeholder="0"
                          step={0.001}
                          controls={true}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Width (in cm)"
                        {...formItemLayout}
                        name="width">
                        <InputNumber
                          className="inputForm"
                          min={0}
                          placeholder="0"
                          step={0.001}
                          controls={true}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Height (in cm)"
                        {...formItemLayout}
                        name="height">
                        <InputNumber
                          className="inputForm"
                          min={0}
                          placeholder="0"
                          step={0.001}
                          controls={true}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Diameter (in cm)"
                        {...formItemLayout}
                        name="diameter">
                        <InputNumber
                          className="inputForm"
                          min={0}
                          onChange={(e) => changeValue('diameter', e)}
                          placeholder="0"
                          step={0.001}
                          precision={3}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="SKU"
                        {...formItemLayout}
                        name={FIELDS.SKU}
                        rules={[
                          {
                            required: true,
                            message: 'Please input Product SKU',
                          },
                        ]}>
                        <Input
                          className="inputForm"
                          disabled={showColorSection}
                          onChange={(e) =>
                            changeValue(FIELDS.SKU, e.target.value)
                          }
                          placeholder="Write product sku here"
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={20}>
                    <Col span={6}>
                      <Form.Item
                        label="Stock"
                        {...formItemLayout}
                        name={FIELDS.QUANTITY}>
                        <Input
                          className="inputForm"
                          disabled
                          min={0}
                          onChange={(e) =>
                            changeValue(FIELDS.QUANTITY, e.target.value)
                          }
                          placeholder="Write product stock here"
                          step="1"
                          type="number"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <div className="infinity-container">
                        <Checkbox
                          checked={showColorSection}
                          onChange={onCheckStock}
                        />
                        <span className="text">More Variants</span>
                      </div>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Limit per order (leave blank for no limit)"
                        {...formItemLayout}
                        name={FIELDS.LIMIT}>
                        <InputNumber
                          className="inputForm"
                          min={0}
                          onChange={(e) => changeValue(FIELDS.LIMIT, e)}
                          placeholder="Write product limit here"
                          step="1"
                          type="number"
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item label="Description" {...formItemLayout}>
                    <ReactQuill
                      defaultValue={get(value, FIELDS.DESCRIPTION, '')}
                      formats={formats}
                      modules={modules}
                      onChange={handleOnChange.bind(this, FIELDS.DESCRIPTION)}
                      placeholder="Write product description here"
                    />
                  </Form.Item>

                  <Row gutter={20}>
                    <Col span={12}>
                      {parentCategoryOption.length > 0 && (
                        <Form.Item
                          className="ant-form-item-required"
                          label="Parent Category"
                          {...formItemLayout}
                          rules={[
                            {
                              required: true,
                              message: 'Please input category',
                            },
                          ]}
                          required>
                          <Select
                            className={
                              selectedParent ? 'inputForm' : 'custom-input-form'
                            }
                            onChange={handleOnChangeWithThreeParams.bind(
                              this,
                              FIELDS.PRODUCT_CATEGORIES_ATTRIBUTES,
                              0
                            )}
                            placeholder="Select parent category"
                            showSearch
                            value={selectedParent}>
                            <Option disabled selected value="">
                              {'Select Parent Category'}
                            </Option>
                            {parentCategoryOption.length > 0 &&
                              parentCategoryOption.map((category) => (
                                <Option
                                  key={category.id}
                                  value={category.title}>
                                  {category.title}
                                </Option>
                              ))}
                          </Select>
                        </Form.Item>
                      )}
                    </Col>
                    <Col span={12}>
                      {selectedParent && brandOption.length > 0 && (
                        <Form.Item
                          label="Brand"
                          name="brand"
                          {...formItemLayout}
                          rules={[
                            {
                              required: true,
                              message: 'Please input Brand',
                            },
                          ]}>
                          <Select
                            className="inputForm"
                            onChange={(value) =>
                              handleOnChange(FIELDS.BRAND_ID, value)
                            }
                            placeholder="Select product brand"
                            showSearch>
                            <Option disabled selected value="">
                              {'Select Brand'}
                            </Option>
                            {brandOption.length > 0 &&
                              brandOption.map((brand) => (
                                <Option key={brand.id} value={brand.title}>
                                  {brand.title}
                                </Option>
                              ))}
                          </Select>
                        </Form.Item>
                      )}
                    </Col>
                  </Row>

                  <Row gutter={20}>
                    <Col span={12}>
                      {get(
                        value,
                        FIELDS.PRODUCT_CATEGORIES_ATTRIBUTES,
                        []
                      )[0] &&
                        categoryOption.length > 0 &&
                        selectedParent !== 'all' && (
                          <Form.Item label="Category" {...formItemLayout}>
                            <Select
                              className={
                                get(
                                  value,
                                  FIELDS.PRODUCT_CATEGORIES_ATTRIBUTES,
                                  []
                                )[1]
                                  ? 'inputForm'
                                  : 'custom-input-form'
                              }
                              onChange={handleOnChangeWithThreeParams.bind(
                                this,
                                FIELDS.PRODUCT_CATEGORIES_ATTRIBUTES,
                                1
                              )}
                              placeholder="Select category"
                              showSearch
                              value={selectedCategory}>
                              <Option selected value="">
                                {'Select Category'}
                              </Option>
                              {categoryOption.length > 0 &&
                                categoryOption.map((category) => (
                                  <Option
                                    key={category.id}
                                    value={category.title}>
                                    {category.title}
                                  </Option>
                                ))}
                            </Select>
                          </Form.Item>
                        )}
                    </Col>

                    <Col span={12}>
                      {get(
                        value,
                        FIELDS.PRODUCT_CATEGORIES_ATTRIBUTES,
                        []
                      )[1] &&
                        subcategoryOption.length > 0 &&
                        selectedCategory !== 'all' && (
                          <Form.Item label="Subcategory" {...formItemLayout}>
                            <Select
                              className={
                                get(
                                  value,
                                  FIELDS.PRODUCT_CATEGORIES_ATTRIBUTES,
                                  []
                                )[1]
                                  ? 'inputForm'
                                  : 'custom-input-form'
                              }
                              onChange={handleOnChangeWithThreeParams.bind(
                                this,
                                FIELDS.PRODUCT_CATEGORIES_ATTRIBUTES,
                                2
                              )}
                              placeholder="Select subcategory"
                              showSearch
                              value={selectedSubcategory}>
                              <Option selected value="">
                                {'Select Subcategory'}
                              </Option>
                              {subcategoryOption.length > 0 &&
                                subcategoryOption.map((category) => (
                                  <Option
                                    key={category.id}
                                    value={category.title}>
                                    {category.title}
                                  </Option>
                                ))}
                            </Select>
                          </Form.Item>
                        )}
                    </Col>
                  </Row>
                </div>
              </div>

              <div className="section-container">
                <div className="inner-section">
                  <Title className="pageSubtitle" level={3}>
                    Keywords
                  </Title>
                  <Row gutter={20}>
                    <Form.Item
                      colon={false}
                      label={keywordsLabel}
                      {...formItemLayout}
                      name={FIELDS.KEYWORDS}
                      rules={[
                        {
                          message: 'Please input the keywords',
                          required: true,
                        },
                      ]}>
                      <Input className="inputForm" />
                    </Form.Item>
                  </Row>
                </div>
              </div>

              <div className="section-container">
                <div className="inner-section">
                  <Title className="pageSubtitle" level={3}>
                    Pricing
                  </Title>
                  <Row gutter={20}>
                    <Col span={12}>
                      <Form.Item
                        label="Base Price"
                        {...formItemLayout}
                        name={FIELDS.PRICE}
                        rules={[
                          {
                            message: 'Please input Price!',
                            required: true,
                          },
                        ]}>
                        <InputNumber
                          className="inputForm"
                          formatter={handleInputNumberFormatter}
                          min={0}
                          onChange={(e) =>
                            changeValue(FIELDS.PRICE, parseInt(e))
                          }
                          parser={handleInputNumberParser}
                          placeholder="IDR 0.00"
                          step={1}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={20}>
                    <Col lg={12}>
                      <Form.Item label="Discount" {...formItemLayout}>
                        <Radio.Group
                          onChange={handleOnChangeDiscountForm}
                          value={discountStatus}>
                          <Radio value={'None'}>No Discount</Radio>
                          <Radio value={'Percentage'}>Percentage</Radio>
                          <Radio value={'Lower Price'}>Lower Price</Radio>
                        </Radio.Group>
                      </Form.Item>
                    </Col>
                  </Row>

                  {discountStatus !== 'None' && (
                    <Row gutter={20}>
                      <Col span={12} style={{ marginBottom: 16 }}>
                        <Input
                          placeholder="Search your discount"
                          prefix={<SearchOutlined />}
                          onChange={(e) => {
                            const value = e.target.value.toLowerCase();
                            setSearchDiscountArea(value);
                          }}
                        />
                      </Col>
                      <Col span={24}>
                        <Table
                          dataSource={selectedDiscountArea.filter((item) =>
                            item.name.toLowerCase().includes(debounceKeyword)
                          )}
                          columns={columns}
                          pagination={false}
                          scroll={{ x: 1550 }}
                        />
                      </Col>
                    </Row>
                  )}
                </div>
              </div>

              <div className="section-container">
                <div className="inner-section">
                  <Title className="pageSubtitle" level={3}>
                    Rent
                  </Title>
                  <Row gutter={20}>
                    <Col lg={12}>
                      <Form.Item {...formItemLayout}>
                        <Radio.Group
                          onChange={changeRentStatus}
                          value={rentStatus}>
                          <Radio value={false}>Non-rentable</Radio>
                          <Radio value={true}>Rentable</Radio>
                        </Radio.Group>
                      </Form.Item>
                    </Col>
                  </Row>
                  {rentStatus && (
                    <>
                      <Row>
                        <Col lg={6}>
                          <Form.Item label="Risk" {...formItemLayout}>
                            <Select
                              value={value.risk_management_id}
                              onChange={(value) => getRiskDetail(value)}
                              placeholder="Select Risk">
                              {riskList.map(({ id, title }) => (
                                <Option key={id} value={id}>
                                  {title}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={20}>
                        <Col lg={24}>
                          <Form.Item
                            label="Duration and Price"
                            {...formItemLayout}>
                            {rentDuration &&
                              rentDuration.map((duration) => (
                                <Row gutter={8} key={duration.id}>
                                  <Col lg={3}>{duration.duration} Days</Col>
                                  <Col lg={9}>
                                    <div className="rule-form">
                                      <InputNumber
                                        className="inputForm input"
                                        defaultValue={get(
                                          rentDurationPrices[duration.id],
                                          'price',
                                          ''
                                        )}
                                        formatter={handleInputNumberFormatter}
                                        min={0}
                                        onChange={handleDurationPrice.bind(
                                          this,
                                          duration.id
                                        )}
                                        parser={handleInputNumberParser}
                                        placeholder="Write lower price here"
                                        step={1}
                                      />
                                    </div>
                                  </Col>
                                  <Col lg={3}>Deposit</Col>
                                  <Col lg={9}>
                                    <div className="rule-form">
                                      <InputNumber
                                        disabled={true}
                                        className="inputForm input"
                                        value={populateDepositRisk(duration.id)}
                                        formatter={handleInputNumberFormatter}
                                        parser={handleInputNumberParser}
                                      />
                                    </div>
                                  </Col>
                                </Row>
                              ))}
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={20}>
                        <Col lg={12}>
                          <Form.Item
                            label="Daily Late Penalty"
                            {...formItemLayout}>
                            <div className="rule-form">
                              <InputNumber
                                className="inputForm input"
                                defaultValue={value.daily_penalty}
                                formatter={handleInputNumberFormatter}
                                min={0}
                                onChange={setLatePenalty}
                                parser={handleInputNumberParser}
                                step={1}
                              />
                            </div>
                          </Form.Item>
                        </Col>
                      </Row>
                    </>
                  )}
                </div>
              </div>
              <div className="section-container">
                <div className="inner-section">
                  <Title className="pageSubtitle" level={3}>
                    Installation
                  </Title>
                  <Row gutter={20}>
                    <Col lg={12}>
                      <Form.Item {...formItemLayout}>
                        <Radio.Group
                          disabled={rentStatus}
                          onChange={(e) => setIsInstallable(e.target.value)}
                          value={isInstallable}>
                          {INSTALLABLE_OPTIONS.map((opt) => (
                            <Radio key={opt.value} value={opt.value}>
                              {opt.label}
                            </Radio>
                          ))}
                        </Radio.Group>
                      </Form.Item>
                    </Col>
                  </Row>
                  {isInstallable && (
                    <>
                      <Row gutter={20}>
                        <Col span={12}>
                          <Form.Item
                            label="Base Price"
                            {...formItemLayout}
                            name={FIELDS.INSTALLATION_FEE}
                            rules={[
                              {
                                message: 'Please input Price!',
                                required: isInstallable,
                              },
                            ]}>
                            <InputNumber
                              className="inputForm"
                              formatter={handleInputNumberFormatter}
                              min={0}
                              onChange={(e) =>
                                changeValue(
                                  FIELDS.INSTALLATION_FEE,
                                  parseInt(e)
                                )
                              }
                              parser={handleInputNumberParser}
                              placeholder="IDR 0.00"
                              step={1}
                              style={{ width: '100%' }}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label="Non Stock SKU"
                            {...formItemLayout}
                            name={FIELDS.NON_STOCK_SKU}>
                            <Input
                              className="inputForm"
                              onChange={(e) =>
                                changeValue(FIELDS.NON_STOCK_SKU, parseInt(e))
                              }
                              placeholder="Write non stock SKU here"
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={20}>
                        <Col lg={12}>
                          <Form.Item label="Discount" {...formItemLayout}>
                            <Radio.Group
                              onChange={(e) =>
                                setCurrentInstallationDiscountType(
                                  e.target.value
                                )
                              }
                              value={currentInstallationDiscountType}>
                              {INSTALLABLE_DISCOUNT_TYPE_OPTIONS.map((opt) => (
                                <Radio value={opt.value} key={opt.value}>
                                  {opt.label}
                                </Radio>
                              ))}
                            </Radio.Group>
                          </Form.Item>
                        </Col>
                      </Row>
                      {currentInstallationDiscountType !==
                        INSTALLATION_DISCOUNT_TYPE.NONE && (
                        <Row gutter={20}>
                          <Col span={12}>
                            <Form.Item {...formItemLayout}>
                              {currentInstallationDiscountType ===
                                INSTALLATION_DISCOUNT_TYPE.PERCENTAGE && (
                                <div className="rule-form">
                                  <InputNumber
                                    className="inputForm input"
                                    defaultValue={
                                      value[
                                        FIELDS.INSTALLATION_DEDUCTION_PERCENTAGE
                                      ]
                                    }
                                    formatter={handlePercentageFormFormatter}
                                    max={100}
                                    min={0}
                                    onChange={(amount) =>
                                      changeValue(
                                        FIELDS.INSTALLATION_DEDUCTION_PERCENTAGE,
                                        amount
                                      )
                                    }
                                    parser={handlePercentageFormParser}
                                    placeholder="Write percentage deduction here"
                                    step={1}
                                  />
                                </div>
                              )}
                              {currentInstallationDiscountType ===
                                INSTALLATION_DISCOUNT_TYPE.FIXED && (
                                <div className="rule-form">
                                  <InputNumber
                                    className="inputForm input"
                                    defaultValue={
                                      value[FIELDS.INSTALLATION_DEDUCTION_FIXED]
                                    }
                                    formatter={handleInputNumberFormatter}
                                    min={0}
                                    onChange={(amount) =>
                                      changeValue(
                                        FIELDS.INSTALLATION_DEDUCTION_FIXED,
                                        amount
                                      )
                                    }
                                    parser={handleInputNumberParser}
                                    placeholder="Write fixed price here"
                                    step={1}
                                  />
                                </div>
                              )}
                            </Form.Item>
                          </Col>
                        </Row>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="section-container">
                <div className="inner-section">
                  <Title className="pageSubtitle" level={3}>
                    Media
                  </Title>
                  <Form.Item {...formItemLayout}>
                    <div className="buttonSection">
                      <PdButton full onClick={addMedia} outline type="primary">
                        Add Media
                      </PdButton>
                    </div>
                    <div id="drag-content-media">
                      <SortableMedia
                        editMedia={editMedia}
                        helperContainer={() =>
                          document.getElementById('drag-content-media')
                        }
                        items={get(
                          value,
                          FIELDS.PRODUCT_GALLERIES_ATTRIBUTES,
                          []
                        ).filter((item) => !item._destroy)}
                        lockAxis="y"
                        onSortEnd={onSortEndMedia}
                        pressDelay={200}
                        removeMedia={removeMedia}
                      />
                    </div>
                  </Form.Item>
                </div>
              </div>

              {showColorSection && (
                <ColorSection
                  data={get(value, FIELDS.PRODUCT_COLORS_ATTRIBUTES, [])}
                  dataModifier={setValue}
                  imageList={get(
                    value,
                    FIELDS.PRODUCT_GALLERIES_ATTRIBUTES,
                    []
                  )}
                />
              )}

              {id && (
                <SizeSection
                  data={get(value, FIELDS.SIZES, [])}
                  dataModifier={setValue}
                  dataModifiedIndicator={setSizeChanged}
                  productDetail={value}
                />
              )}

              <div className="button-container">
                <Form.Item {...buttonItemLayout}>
                  <div className="buttonSection mt-40">
                    <PdButton
                      disabled={hasErrors()}
                      full
                      htmlType="submit"
                      loading={loadingButton}
                      type="primary">
                      Publish
                    </PdButton>
                  </div>
                </Form.Item>
              </div>
            </Form>
          </Content>
        </div>
      </div>
    </Layout>
  );
}

export default ProductCreateEdit;
